import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calculator, 
  Loader, 
  CheckCircle, 
  AlertCircle, 
  TrendingUp, 
  Thermometer,
  Globe,
  Orbit,
  Star,
  Save,
  Download,
  RotateCcw
} from 'lucide-react';
import { exoplanetAPI } from '../services/api';

const AnimatedStars = () => (
  <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
    {[...Array(80)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full bg-white opacity-30"
        style={{
          width: `${Math.random() * 2 + 1}px`,
          height: `${Math.random() * 2 + 1}px`,
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
        }}
        animate={{
          opacity: [0.2, 0.5, 0.2],
          y: [0, Math.random() * 20 - 10, 0],
        }}
        transition={{
          duration: Math.random() * 6 + 4,
          repeat: Infinity,
          repeatType: 'loop',
          delay: Math.random() * 4,
        }}
      />
    ))}
  </div>
);

const PredictionForm = () => {
  const [formData, setFormData] = useState({
    planetRadius: 1.0,
    orbitalPeriod: 365,
    stellarMass: 1.0,
    equilibriumTemp: 288,
    eccentricity: 0.1,
    stellarRadius: 1.0
  });

  const [prediction, setPrediction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [csvVector, setCsvVector] = useState(null); // coerced 52-length vector from CSV
  const [csvError, setCsvError] = useState(null);
  const [csvNotice, setCsvNotice] = useState(null); // info about trim/pad

  const parameterInfo = {
    planetRadius: {
      label: 'Planet Radius',
      unit: 'Earth Radii (RE)',
      range: '0.1 - 10.0',
      optimal: '0.8 - 1.5',
      description: 'Size relative to Earth. Affects gravity and atmosphere retention.',
      icon: Globe
    },
    orbitalPeriod: {
      label: 'Orbital Period',
      unit: 'Days',
      range: '1 - 10000',
      optimal: '200 - 700',
      description: 'Time to complete one orbit. Indicates distance from star.',
      icon: Orbit
    },
    stellarMass: {
      label: 'Stellar Mass',
      unit: 'Solar Masses (M☉)',
      range: '0.1 - 5.0',
      optimal: '0.5 - 1.5',
      description: 'Mass of the host star. Affects luminosity and lifetime.',
      icon: Star
    },
    equilibriumTemp: {
      label: 'Equilibrium Temperature',
      unit: 'Kelvin (K)',
      range: '50 - 1000',
      optimal: '273 - 323',
      description: 'Theoretical surface temperature without atmosphere.',
      icon: Thermometer
    },
    eccentricity: {
      label: 'Orbital Eccentricity',
      unit: 'Dimensionless',
      range: '0.0 - 0.9',
      optimal: '0.0 - 0.3',
      description: 'How elliptical the orbit is. 0 = circular, higher = more elliptical.',
      icon: Orbit
    },
    stellarRadius: {
      label: 'Stellar Radius',
      unit: 'Solar Radii (R☉)',
      range: '0.1 - 5.0',
      optimal: '0.7 - 1.3',
      description: 'Size of the host star relative to the Sun.',
      icon: Star
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
    
    // Clear previous results when form changes
    if (prediction) {
      setPrediction(null);
      setIsSaved(false);
    }
  };

  const handlePresetLoad = (presetName) => {
    const presets = {
      earth: {
        planetRadius: 1.0,
        orbitalPeriod: 365.25,
        stellarMass: 1.0,
        equilibriumTemp: 288,
        eccentricity: 0.017,
        stellarRadius: 1.0
      },
      'kepler-186f': {
        planetRadius: 1.11,
        orbitalPeriod: 129.9,
        stellarMass: 0.54,
        equilibriumTemp: 188,
        eccentricity: 0.04,
        stellarRadius: 0.51
      },
      'mars': {
        planetRadius: 0.53,
        orbitalPeriod: 687,
        stellarMass: 1.0,
        equilibriumTemp: 210,
        eccentricity: 0.093,
        stellarRadius: 1.0
      },
      'venus': {
        planetRadius: 0.95,
        orbitalPeriod: 225,
        stellarMass: 1.0,
        equilibriumTemp: 737,
        eccentricity: 0.007,
        stellarRadius: 1.0
      }
    };

    if (presets[presetName]) {
      setFormData(presets[presetName]);
      setPrediction(null);
      setIsSaved(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      let result;
      if (Array.isArray(csvVector) && csvVector.length > 0) {
        // Use backend API for CSV vector
        result = await exoplanetAPI.predictHabitability({ csv_vector: csvVector });
      } else {
        // Use backend API for manual inputs
        result = await exoplanetAPI.predictHabitability(formData);
      }
      setPrediction(result);
    } catch (err) {
      const message = (err && err.message) ? err.message : 'Failed to predict habitability. Please try again.';
      setError(message);
      console.error('Prediction error (all paths failed):', err);
    } finally {
      setIsLoading(false);
    }
  };

  // CSV handling: expects a single row with 52 numeric values
  const handleCsvUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setCsvError(null);
    setCsvVector(null);
    setPrediction(null);
    setIsSaved(false);
    setCsvNotice(null);

    try {
      const text = await file.text();
      // Support CSV with header or without; take the last non-empty line
      const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      if (lines.length === 0) throw new Error('Empty CSV');

      // If there are multiple lines, pick the last one as the data row
      const dataRow = lines[lines.length - 1];
      const parts = dataRow.split(',').map((p) => p.trim());

      // If header present, try the second line
      if (parts.some((p) => isNaN(Number(p))) && lines.length >= 2) {
        const possibleRow = lines[1].split(',').map((p) => p.trim());
        if (possibleRow.every((p) => !isNaN(Number(p)))) {
          parts.length = 0;
          Array.prototype.push.apply(parts, possibleRow);
        }
      }

      const parsed = parts.map((p) => Number(p)).filter((n) => Number.isFinite(n));
      let coerced = parsed.slice(0, 52);
      if (coerced.length < 52) {
        coerced = coerced.concat(Array(52 - coerced.length).fill(0));
      }

      if (parsed.length > 52) {
        setCsvNotice(`CSV had ${parsed.length} features; using first 52.`);
      } else if (parsed.length < 52) {
        setCsvNotice(`CSV had ${parsed.length} features; padded ${52 - parsed.length} zeros.`);
      } else {
        setCsvNotice(null);
      }

      setCsvVector(coerced);
    } catch (err) {
      console.error('CSV parse error:', err);
      setCsvError(err.message || 'Invalid CSV format');
    }
  };

  const handleSave = async () => {
    if (!prediction) return;

    try {
      const planetData = {
        ...formData,
        ...prediction,
        name: `Custom Planet ${Date.now()}`,
        customPrediction: true
      };

      await exoplanetAPI.saveExoplanet(planetData);
      setIsSaved(true);
      
      // Show success message briefly
      setTimeout(() => setIsSaved(false), 3000);
    } catch (err) {
      console.error('Save error:', err);
    }
  };

  const handleReset = () => {
    setFormData({
      planetRadius: 1.0,
      orbitalPeriod: 365,
      stellarMass: 1.0,
      equilibriumTemp: 288,
      eccentricity: 0.1,
      stellarRadius: 1.0
    });
    setPrediction(null);
    setError(null);
    setIsSaved(false);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 30) return 'text-orange-400';
    return 'text-red-400';
  };

  const getScoreBg = (score) => {
    if (score >= 80) return 'bg-green-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 30) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex-1 min-h-screen relative bg-gradient-to-br from-black via-blue-950 to-black overflow-x-hidden">
      <AnimatedStars />
      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-8 py-16">
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-5xl font-extrabold text-white mb-6 tracking-wide drop-shadow-lg text-center"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          Habitability <span className="text-blue-400">Predictor</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="text-xl text-blue-200 font-light mb-10 text-center max-w-2xl mx-auto"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          Enter planetary parameters to predict habitability using our advanced machine learning model trained on NASA's exoplanet database and peer-reviewed research.
        </motion.p>
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-3xl bg-black/50 backdrop-blur-2xl border border-blue-900/40 rounded-2xl shadow-2xl p-10 flex flex-col gap-8"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          {/* Parameter Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {Object.entries(parameterInfo).map(([key, info]) => {
              const Icon = info.icon;
              return (
                <div key={key} className="bg-black/40 rounded-xl p-6 flex flex-col gap-2 shadow-lg border border-blue-900/30">
                  <div className="flex items-center gap-3 mb-2">
                    <Icon className="w-6 h-6 text-blue-400" />
                    <span className="text-lg text-white font-semibold tracking-wide">{info.label}</span>
                  </div>
                  <input
                    type="number"
                    step="any"
                    min={parseFloat(info.range.split('-')[0])}
                    max={parseFloat(info.range.split('-')[1])}
                    value={formData[key]}
                    onChange={e => handleInputChange(key, e.target.value)}
                    className="bg-black/30 border border-blue-700/40 focus:border-blue-400 text-white rounded-lg px-4 py-3 text-lg outline-none transition-all duration-200 shadow-inner focus:shadow-blue-400/10"
                  />
                  <div className="flex justify-between text-xs text-blue-200 mt-1">
                    <span>Range: {info.range}</span>
                    <span>Optimal: {info.optimal}</span>
                  </div>
                  <div className="text-xs text-blue-300 mt-1">{info.description}</div>
                </div>
              );
            })}
          </div>
          {/* CSV Upload */}
          <div className="flex flex-col gap-2 mt-2">
            <label className="text-blue-200 text-sm">Upload CSV with 52 features</label>
            <input type="file" accept=".csv" onChange={handleCsvUpload} className="text-blue-200" />
            {csvVector && (
              <span className="text-green-400 text-sm">CSV loaded: 52 features detected</span>
            )}
            {csvError && (
              <span className="text-red-400 text-sm">{csvError}</span>
            )}
            {csvNotice && !csvError && (
              <span className="text-yellow-300 text-sm">{csvNotice}</span>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col md:flex-row gap-4 justify-center items-center mt-4">
            <button
              type="submit"
              disabled={isLoading}
              className="bg-gradient-to-r from-blue-700 to-blue-500 hover:from-blue-600 hover:to-blue-400 text-white px-10 py-4 rounded-full font-bold text-lg shadow-lg transition-all duration-300 disabled:opacity-60"
            >
              {isLoading ? 'Predicting...' : 'Predict Habitability'}
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="bg-black/40 hover:bg-blue-900/40 text-blue-200 px-8 py-4 rounded-full font-semibold text-lg shadow transition-all duration-300 border border-blue-900/40"
            >
              Reset
            </button>
          </div>
          {/* Prediction Result */}
          {prediction && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="flex flex-col items-center gap-4 mt-8"
            >
              <span className="text-2xl text-white font-bold tracking-wide">Habitability Score</span>
              <div className="w-40 h-40 rounded-full bg-gradient-to-br from-blue-500 via-blue-700 to-black flex items-center justify-center shadow-2xl border-4 border-blue-400/40">
                <span className="text-5xl font-extrabold text-white drop-shadow-lg">{prediction.habitabilityScore}%</span>
              </div>
              <span className="text-lg text-blue-200 font-light">{prediction.habitability}</span>
              <button
                type="button"
                onClick={handleSave}
                className="bg-gradient-to-r from-blue-700 to-blue-500 hover:from-blue-600 hover:to-blue-400 text-white px-8 py-3 rounded-full font-semibold text-lg shadow transition-all duration-300"
              >
                Save Prediction
              </button>
            </motion.div>
          )}
          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-400 text-center mt-4 font-semibold"
            >
              {error}
            </motion.div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PredictionForm; 